import React from 'react';
import { ObjectModel } from '../items/hooks/HObjects';
import { User } from '../items/interfaces/IUsers';
import {
    Button,
    Chip,
    Select,
    MenuItem,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    Typography,
} from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { LoadingSpinner } from '../utils/LoadingSpinner';
import ObjectQuickForm from '../dialogs/ObjectQuickForm';
import ObjectAccessRequestDialog from '../dialogs/ObjectAccessDialog';
import ObjectsDB from '../dashboards/ObjectsDB';

interface DealsSectionProps {
    displayedObjects: ObjectModel[];
    loading: boolean;
    typeFilter: string;
    setTypeFilter: (v: string) => void;
    typeOptions: string[];
    statusFilter: string;
    setStatusFilter: (v: string) => void;
    statusOptions: string[];
    zoningFilter: string;
    setZoningFilter: (v: string) => void;
    zoningOptions: string[];
    searchInput: string;
    handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleAddObject: () => void;
    formDialogOpen: boolean;
    setFormDialogOpen: (b: boolean) => void;
    formData: Partial<ObjectModel>;
    setFormData: (d: Partial<ObjectModel>) => void;
    handleFormSubmit: () => void;
    accessRequestDialogOpen: boolean;
    accessRequestObjectId: string | number | null;
    setAccessRequestDialogOpen: (b: boolean) => void;
    setAccessRequestObjectId: (id: string | number | null) => void;
    handleAccessRequestSubmit: () => void;
    deleteDialogOpen: boolean;
    setDeleteDialogOpen: (b: boolean) => void;
    deleteTarget: ObjectModel | null;
    handleDeleteConfirm: () => void;
    selectedRows: any;
    toggleRowSelection: (id: string|number) => void;
    selectAllRows: (select: boolean) => void;
    handleEditObject: (object: ObjectModel, updatedData: Partial<ObjectModel>) => Promise<void>;
    handleDeleteClick: (object: ObjectModel) => void;
    user?: User | null;
    handleOpenObjectDetails: (object: ObjectModel) => void;
    snackbar: { open: boolean; message: string; severity: 'success' | 'error' };
    setSnackbar: (s: { open: boolean; message: string; severity: 'success' | 'error' }) => void;
    assetClassOptions: readonly string[];
}

export default function DealsSection(props: DealsSectionProps) {
    const {
        displayedObjects,
        loading,
        typeFilter,
        setTypeFilter,
        typeOptions,
        statusFilter,
        setStatusFilter,
        statusOptions,
        zoningFilter,
        setZoningFilter,
        zoningOptions,
        searchInput,
        handleSearchChange,
        handleAddObject,
        formDialogOpen,
        setFormDialogOpen,
        formData,
        setFormData,
        handleFormSubmit,
        accessRequestDialogOpen,
        accessRequestObjectId,
        setAccessRequestDialogOpen,
        setAccessRequestObjectId,
        handleAccessRequestSubmit,
        deleteDialogOpen,
        setDeleteDialogOpen,
        deleteTarget,
        handleDeleteConfirm,
        selectedRows,
        toggleRowSelection,
        selectAllRows,
        handleEditObject,
        handleDeleteClick,
        user,
        handleOpenObjectDetails,
        snackbar,
        setSnackbar,
        assetClassOptions,
    } = props;

    return (
        <>
            <div className='deals-title-box'>
                <h1 style={{ margin: 0 }}>Deals </h1>
                {!loading && (
                    <Chip
                        label={`${displayedObjects.length} object${displayedObjects.length !== 1 ? 's' : ''}`}
                        size='small'
                        color='primary'
                        variant='outlined'
                    />
                )}
            </div>

            <div className='header-flex'>
                <Select
                    size='small'
                    displayEmpty
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(String(e.target.value))}
                    className='gold-select'
                >
                    <MenuItem value=''>All Types</MenuItem>
                    {typeOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </Select>
                <Select
                    size='small'
                    displayEmpty
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(String(e.target.value))}
                    className='gold-select'
                >
                    <MenuItem value=''>All Statuses</MenuItem>
                    {statusOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </Select>
                <Select
                    size='small'
                    displayEmpty
                    value={zoningFilter}
                    onChange={(e) => setZoningFilter(String(e.target.value))}
                    className='gold-select'
                >
                    <MenuItem value=''>All Zonings</MenuItem>
                    {zoningOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </Select>
                <TextField
                    size='small'
                    placeholder='Search objects by name...'
                    value={searchInput}
                    onChange={handleSearchChange}
                    className='search-field'
                />
                <Button
                    variant='contained'
                    onClick={handleAddObject}
                    className='gold-button'
                >
                    New Deal
                </Button>
            </div>

            <ObjectQuickForm
                open={formDialogOpen}
                onClose={() => setFormDialogOpen(false)}
                formData={formData}
                setFormData={setFormData}
                assetClassOptions={assetClassOptions}
                onSubmit={handleFormSubmit}
            />

            <ObjectAccessRequestDialog
                open={accessRequestDialogOpen}
                objectId={accessRequestObjectId}
                user={user}
                onClose={() => {
                    setAccessRequestDialogOpen(false);
                    setAccessRequestObjectId(null);
                }}
                onSubmit={handleAccessRequestSubmit}
            />

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete object "{deleteTarget?.name}"?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant='contained'
                        color='error'
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {loading ? (
                <LoadingSpinner text='Loading objects...' />
            ) : displayedObjects.length === 0 ? (
                <div className='deals-empty-state'>
                    <SearchOffIcon className='deals-empty-icon' />
                    <Typography variant='h6' color='text.secondary'>
                        No objects found
                    </Typography>
                    <Typography variant='body2' color='text.disabled'>
                        {/* eslint-disable-next-line react/no-unescaped-entities */}
                        {searchInput ? `No results for "${searchInput}"` : 'There are no objects matching the current filter.'}
                    </Typography>
                </div>
            ) : (
                <>
                    <ObjectsDB
                        objects={displayedObjects}
                        selectedRows={selectedRows}
                        onToggleSelect={toggleRowSelection}
                        onToggleSelectAll={(select) => selectAllRows(select)}
                        onEdit={handleEditObject}
                        onDelete={handleDeleteClick}
                        user={user}
                        onRowClick={handleOpenObjectDetails}
                    />
                </>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
